import React, { useEffect, useMemo, useState } from "react";
import {
  getCategories,
  addCategory,
  updateCategory,
  deleteCategory,
  addSubcategory,
  updateSubcategory,
  deleteSubcategory,
} from "../common/api/api";

const inputCls =
  "w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 text-sm";

const blankCategory = { name: "", slug: "", description: "", accent: "#00d4ff", active: true };
const blankSub = { name: "", slug: "", description: "", active: true };

const slugify = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [categoryDraft, setCategoryDraft] = useState(blankCategory);
  const [editingCategoryId, setEditingCategoryId] = useState("");
  const [subcategoryDrafts, setSubcategoryDrafts] = useState({});
  const [editingSub, setEditingSub] = useState({});

  const loadCategories = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Failed to load categories");
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const activeCount = useMemo(
    () => categories.filter((item) => item.active !== false).length,
    [categories]
  );

  const resetCategoryForm = () => {
    setEditingCategoryId("");
    setCategoryDraft(blankCategory);
  };

  const handleSaveCategory = async () => {
    if (!categoryDraft.name.trim()) return alert("Category name is required");
    setSaving(true);
    const payload = {
      ...categoryDraft,
      slug: slugify(categoryDraft.slug || categoryDraft.name),
    };
    const resp = editingCategoryId
      ? await updateCategory(editingCategoryId, payload)
      : await addCategory(payload);
    setSaving(false);
    if (resp.success) {
      resetCategoryForm();
      loadCategories();
    } else {
      alert(resp.error || "Failed to save category");
    }
  };

  const startEditCategory = (category) => {
    setEditingCategoryId(category._id);
    setCategoryDraft({
      name: category.name || "",
      slug: category.slug || "",
      description: category.description || "",
      accent: category.accent || "#00d4ff",
      active: category.active !== false,
    });
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Delete this main category and all of its subcategories?")) return;
    const resp = await deleteCategory(categoryId);
    if (!resp.success) return alert(resp.error || "Failed to delete category");
    loadCategories();
  };

  const setDraftFor = (categoryId, data) => {
    setSubcategoryDrafts((prev) => ({ ...prev, [categoryId]: data }));
  };

  const startEditSub = (categoryId, sub) => {
    setEditingSub({
      categoryId,
      subcategoryId: sub._id,
      name: sub.name || "",
      slug: sub.slug || "",
      description: sub.description || "",
      active: sub.active !== false,
    });
  };

  const cancelEditSub = () => {
    setEditingSub({});
  };

  const saveSubcategory = async (categoryId) => {
    const draft = subcategoryDrafts[categoryId] || blankSub;
    if (!draft.name?.trim()) return alert("Subcategory name is required");
    setSaving(true);
    const resp = await addSubcategory(categoryId, {
      ...draft,
      slug: slugify(draft.slug || draft.name),
    });
    setSaving(false);
    if (resp.success) {
      setDraftFor(categoryId, blankSub);
      loadCategories();
    } else {
      alert(resp.error || "Failed to add subcategory");
    }
  };

  const saveEditedSubcategory = async () => {
    if (!editingSub.name?.trim()) return alert("Subcategory name is required");
    setSaving(true);
    const resp = await updateSubcategory(editingSub.categoryId, editingSub.subcategoryId, {
      ...editingSub,
      slug: slugify(editingSub.slug || editingSub.name),
    });
    setSaving(false);
    if (resp.success) {
      cancelEditSub();
      loadCategories();
    } else {
      alert(resp.error || "Failed to update subcategory");
    }
  };

  const handleDeleteSub = async (categoryId, subcategoryId) => {
    if (!window.confirm("Delete this subcategory?")) return;
    const resp = await deleteSubcategory(categoryId, subcategoryId);
    if (!resp.success) return alert(resp.error || "Failed to delete subcategory");
    loadCategories();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Categories</h1>
          <p className="text-sm text-[#4a5568] mt-1">Manage main categories and their subcategories from one place.</p>
        </div>
        <div className="flex gap-3 text-xs">
          <div className="px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-[#a0aec0]">
            {categories.length} main categories
          </div>
          <div className="px-3 py-2 rounded-xl border border-[rgba(0,212,255,0.2)] bg-[rgba(0,212,255,0.08)] text-[#00d4ff]">
            {activeCount} active
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-6 items-start">
        <div className="rounded-2xl border border-white/[0.08] bg-[#0a0f1e] p-5 space-y-4">
          <div>
            <h2 className="text-white font-bold text-lg">{editingCategoryId ? "Edit Main Category" : "Add Main Category"}</h2>
            <p className="text-xs text-[#4a5568] mt-1">Create a top-level category and attach subcategories under it.</p>
          </div>

          <div>
            <label className="text-xs text-[#a0aec0] mb-1 block">Name</label>
            <input
              className={inputCls}
              value={categoryDraft.name}
              onChange={(e) => setCategoryDraft({ ...categoryDraft, name: e.target.value, slug: categoryDraft.slug || slugify(e.target.value) })}
              placeholder="e.g. Tablets"
            />
          </div>
          <div>
            <label className="text-xs text-[#a0aec0] mb-1 block">Slug</label>
            <input
              className={inputCls}
              value={categoryDraft.slug}
              onChange={(e) => setCategoryDraft({ ...categoryDraft, slug: slugify(e.target.value) })}
              placeholder="e.g. tablets"
            />
          </div>
          <div>
            <label className="text-xs text-[#a0aec0] mb-1 block">Description</label>
            <textarea
              className={`${inputCls} min-h-[90px] resize-y`}
              value={categoryDraft.description}
              onChange={(e) => setCategoryDraft({ ...categoryDraft, description: e.target.value })}
              placeholder="Short description for internal/admin use"
            />
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label className="text-xs text-[#a0aec0] mb-1 block">Accent</label>
              <input
                className={inputCls}
                value={categoryDraft.accent}
                onChange={(e) => setCategoryDraft({ ...categoryDraft, accent: e.target.value })}
                placeholder="#00d4ff"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-[#a0aec0] pb-2">
              <input
                type="checkbox"
                checked={categoryDraft.active}
                onChange={(e) => setCategoryDraft({ ...categoryDraft, active: e.target.checked })}
              />
              Active
            </label>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={handleSaveCategory}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#00d4ff] to-[#0099cc] text-[#060c18] font-bold text-sm hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "Saving..." : editingCategoryId ? "Update Category" : "Add Category"}
            </button>
            {editingCategoryId && (
              <button
                onClick={resetCategoryForm}
                className="px-4 py-2 rounded-lg border border-white/[0.1] text-[#a0aec0] text-sm hover:border-white/[0.2]"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {loading ? (
            <div className="rounded-2xl border border-white/[0.08] bg-[#0a0f1e] p-8 text-[#4a5568] text-sm">Loading categories...</div>
          ) : error ? (
            <div className="rounded-2xl border border-red-400/20 bg-red-400/5 p-8 text-red-400 text-sm">{error}</div>
          ) : categories.length === 0 ? (
            <div className="rounded-2xl border border-white/[0.08] bg-[#0a0f1e] p-8 text-[#4a5568] text-sm">No categories found.</div>
          ) : (
            categories.map((category) => {
              const draft = subcategoryDrafts[category._id] || blankSub;
              const subEditOpen = editingSub.categoryId === category._id;
              return (
                <div key={category._id} className="rounded-2xl border border-white/[0.08] bg-[#0a0f1e] overflow-hidden">
                  <div className="p-5 border-b border-white/[0.06] flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <span className="w-3 h-12 rounded-full mt-1" style={{ background: category.accent || "#00d4ff" }} />
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-white text-lg font-bold">{category.name}</h3>
                          <span className="px-2 py-0.5 rounded-full text-[10px] border border-white/[0.1] text-[#a0aec0]">/{category.slug}</span>
                          {category.active === false && <span className="px-2 py-0.5 rounded-full text-[10px] border border-red-400/20 text-red-400">inactive</span>}
                        </div>
                        <p className="text-sm text-[#4a5568] mt-1">{category.description || "No description"}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => startEditCategory(category)} className="px-3 py-1.5 rounded-lg text-xs border border-[rgba(0,212,255,0.2)] text-[#00d4ff] hover:bg-[rgba(0,212,255,0.08)]">Edit</button>
                      <button onClick={() => handleDeleteCategory(category._id)} className="px-3 py-1.5 rounded-lg text-xs border border-red-400/20 text-red-400 hover:bg-red-400/10">Delete</button>
                    </div>
                  </div>

                  <div className="p-5 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="text-sm font-bold text-[#e2e8f0]">Subcategories ({category.subcategories?.length || 0})</h4>
                      <span className="text-[11px] text-[#4a5568]">Main slug used when no subcategory is selected</span>
                    </div>

                    <div className="grid gap-2">
                      {(category.subcategories || []).map((sub) => {
                        const isEditing = subEditOpen && editingSub.subcategoryId === sub._id;
                        return (
                          <div key={sub._id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                            {isEditing ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs text-[#a0aec0] mb-1 block">Name</label>
                                  <input className={inputCls} value={editingSub.name || ""} onChange={(e) => setEditingSub({ ...editingSub, name: e.target.value })} />
                                </div>
                                <div>
                                  <label className="text-xs text-[#a0aec0] mb-1 block">Slug</label>
                                  <input className={inputCls} value={editingSub.slug || ""} onChange={(e) => setEditingSub({ ...editingSub, slug: slugify(e.target.value) })} />
                                </div>
                                <div className="md:col-span-2">
                                  <label className="text-xs text-[#a0aec0] mb-1 block">Description</label>
                                  <input className={inputCls} value={editingSub.description || ""} onChange={(e) => setEditingSub({ ...editingSub, description: e.target.value })} />
                                </div>
                                <label className="flex items-center gap-2 text-xs text-[#a0aec0]">
                                  <input type="checkbox" checked={editingSub.active !== false} onChange={(e) => setEditingSub({ ...editingSub, active: e.target.checked })} />
                                  Active
                                </label>
                                <div className="flex gap-2 md:justify-end">
                                  <button onClick={saveEditedSubcategory} className="px-3 py-2 rounded-lg text-xs bg-[rgba(0,212,255,0.14)] text-[#00d4ff] border border-[rgba(0,212,255,0.25)]">Save</button>
                                  <button onClick={cancelEditSub} className="px-3 py-2 rounded-lg text-xs border border-white/[0.1] text-[#a0aec0]">Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-semibold text-white">{sub.name}</span>
                                    <span className="px-2 py-0.5 rounded-full text-[10px] border border-white/[0.08] text-[#a0aec0]">/{sub.slug}</span>
                                    {sub.active === false && <span className="px-2 py-0.5 rounded-full text-[10px] border border-red-400/20 text-red-400">inactive</span>}
                                  </div>
                                  <p className="text-xs text-[#4a5568] mt-1">{sub.description || "No description"}</p>
                                </div>
                                <div className="flex gap-2">
                                  <button onClick={() => startEditSub(category._id, sub)} className="px-3 py-1.5 rounded-lg text-xs border border-[rgba(0,212,255,0.2)] text-[#00d4ff] hover:bg-[rgba(0,212,255,0.08)]">Edit</button>
                                  <button onClick={() => handleDeleteSub(category._id, sub._id)} className="px-3 py-1.5 rounded-lg text-xs border border-red-400/20 text-red-400 hover:bg-red-400/10">Delete</button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="rounded-xl border border-dashed border-white/[0.12] p-4 bg-white/[0.015]">
                      <h5 className="text-sm font-semibold text-white mb-3">Add Subcategory</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-[#a0aec0] mb-1 block">Name</label>
                          <input className={inputCls} value={draft.name || ""} onChange={(e) => setDraftFor(category._id, { ...draft, name: e.target.value, slug: draft.slug || slugify(e.target.value) })} placeholder="e.g. iPad" />
                        </div>
                        <div>
                          <label className="text-xs text-[#a0aec0] mb-1 block">Slug</label>
                          <input className={inputCls} value={draft.slug || ""} onChange={(e) => setDraftFor(category._id, { ...draft, slug: slugify(e.target.value) })} placeholder="e.g. ipad" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-xs text-[#a0aec0] mb-1 block">Description</label>
                          <input className={inputCls} value={draft.description || ""} onChange={(e) => setDraftFor(category._id, { ...draft, description: e.target.value })} placeholder="Optional short description" />
                        </div>
                        <label className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <input type="checkbox" checked={draft.active !== false} onChange={(e) => setDraftFor(category._id, { ...draft, active: e.target.checked })} />
                          Active
                        </label>
                        <div className="md:justify-end flex">
                          <button onClick={() => saveSubcategory(category._id)} disabled={saving} className="px-4 py-2 rounded-lg bg-[rgba(0,212,255,0.14)] text-[#00d4ff] border border-[rgba(0,212,255,0.25)] text-sm disabled:opacity-50">
                            {saving ? "Saving..." : "Add Subcategory"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCategories;
